import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  Heart,
  Coffee,
  Star,
  Trophy,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Sparkles,
  Users,
  CreditCard
} from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const emojiIcons = {
  coffee: Coffee,
  heart: Heart,
  star: Star,
  trophy: Trophy,
};

const Donation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [pollStatus, setPollStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const sessionId = searchParams.get("session_id");
  const method = searchParams.get("method");

  useEffect(() => {
    axios.get(`${API}/donation/packages`).then((res) => setPackages(res.data));
    axios.get(`${API}/donation/stats`).then((res) => setStats(res.data)).catch(() => {});
  }, []);

  // Poll Stripe status
  useEffect(() => {
    if (!sessionId || method === "paypal") return;

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { setPollStatus("timeout"); return; }
      attempts++;
      try {
        const res = await axios.get(`${API}/donation/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setPollStatus("success"); return; }
        if (res.data.status === "expired") { setPollStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setPollStatus("error"); }
    };
    setPollStatus("checking");
    poll();
  }, [sessionId, method]);

  // PayPal return handling
  useEffect(() => {
    if (method !== "paypal") return;
    const token = searchParams.get("token");
    if (!token) return;

    setPollStatus("checking");
    axios.post(`${API}/donation/paypal/capture/${token}`)
      .then((res) => {
        setPollStatus(res.data.payment_status === "paid" ? "success" : "error");
      })
      .catch(() => setPollStatus("error"));
  }, [method, searchParams]);

  const getAmount = () => {
    if (selected) return packages.find((p) => p.id === selected)?.amount;
    if (customAmount) return parseFloat(customAmount);
    return null;
  };

  const handleStripe = async () => {
    const body = { origin_url: window.location.origin };
    if (selected) body.package_id = selected;
    else if (customAmount) {
      const a = parseFloat(customAmount);
      if (!a || a < 1 || a > 1000) { toast.error("Montant entre 1€ et 1000€"); return; }
      body.custom_amount = a;
    } else { toast.error("Choisissez un montant"); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/donation/checkout`, body);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
      setLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    const body = { origin_url: window.location.origin };
    if (selected) body.package_id = selected;
    else if (customAmount) {
      const a = parseFloat(customAmount);
      if (!a || a < 1 || a > 1000) { toast.error("Montant entre 1€ et 1000€"); throw new Error(); }
      body.custom_amount = a;
    } else { toast.error("Choisissez un montant"); throw new Error(); }

    const res = await axios.post(`${API}/donation/paypal/create`, body);
    return res.data.order_id;
  };

  const onPayPalApprove = async (data) => {
    try {
      const res = await axios.post(`${API}/donation/paypal/capture/${data.orderID}`);
      if (res.data.payment_status === "paid") {
        setPollStatus("success");
        toast.success("Merci pour votre don !");
      } else {
        toast.error("Le paiement n'a pas abouti");
      }
    } catch {
      toast.error("Erreur lors de la capture du paiement");
    }
  };

  // Simple header for anonymous users
  const AnonHeader = () => (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="tricolore-stripe"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-red-500" />
            <span className="font-serif font-bold text-xl text-blue-900">CiviquePrep</span>
          </Link>
          <Link to="/auth">
            <Button variant="outline" size="sm">Se connecter</Button>
          </Link>
        </div>
      </div>
    </nav>
  );

  const Header = user ? Navbar : AnonHeader;

  // Success / Status screen
  if (pollStatus) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="donation-status-page">
        <Header />
        <main className="max-w-lg mx-auto px-4 py-16 text-center">
          {pollStatus === "checking" && (
            <div className="space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-blue-900 mx-auto" />
              <p className="text-lg text-slate-600">Vérification du paiement...</p>
            </div>
          )}
          {pollStatus === "success" && (
            <Card>
              <CardContent className="p-8 space-y-4">
                <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-slate-900">Merci pour votre don !</h2>
                <p className="text-slate-600">
                  Votre générosité contribue à maintenir CiviquePrep gratuit et accessible à tous.
                </p>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-blue-900 hover:bg-blue-800 mt-4"
                  data-testid="back-to-dashboard-btn"
                >
                  Retour au tableau de bord
                </Button>
              </CardContent>
            </Card>
          )}
          {(pollStatus === "error" || pollStatus === "timeout" || pollStatus === "expired") && (
            <Card>
              <CardContent className="p-8 space-y-4">
                <p className="text-lg text-slate-600">
                  {pollStatus === "expired" ? "La session a expiré." : "Impossible de vérifier le paiement."}
                </p>
                <Button onClick={() => { setPollStatus(null); navigate("/donation"); }} className="bg-blue-900 hover:bg-blue-800">
                  Réessayer
                </Button>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    );
  }

  const amountReady = selected || (customAmount && parseFloat(customAmount) >= 1);

  return (
    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR" }}>
      <div className="min-h-screen bg-slate-50" data-testid="donation-page">
        <Header />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-slate-600">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>

          {/* Hero */}
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-slate-900 mb-2">
              Soutenez CiviquePrep
            </h1>
            <p className="text-slate-600 max-w-md mx-auto">
              CiviquePrep est gratuit. Votre don aide à maintenir la plateforme et à la rendre accessible à tous.
            </p>
          </div>

          {/* Donor Counter */}
          {stats && stats.donors_count > 0 && (
            <div className="flex items-center justify-center gap-6 mb-8 py-4 px-6 bg-white rounded-xl border border-slate-200" data-testid="donation-stats">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-900" />
                <div>
                  <p className="text-xl font-bold text-slate-900" data-testid="donors-count">{stats.donors_count}</p>
                  <p className="text-xs text-slate-500">donateur{stats.donors_count > 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="w-px h-10 bg-slate-200" />
              <div>
                <p className="text-xl font-bold text-slate-900" data-testid="total-raised">{stats.total_raised.toFixed(2)}€</p>
                <p className="text-xs text-slate-500">récoltés</p>
              </div>
            </div>
          )}

          {/* Amount cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {packages.map((pkg) => {
              const Icon = emojiIcons[pkg.emoji] || Heart;
              const isSelected = selected === pkg.id;
              return (
                <button
                  key={pkg.id}
                  onClick={() => { setSelected(isSelected ? null : pkg.id); setCustomAmount(""); }}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    isSelected ? "border-blue-900 bg-blue-50 shadow-md" : "border-slate-200 bg-white hover:border-blue-300"
                  }`}
                  data-testid={`donation-pkg-${pkg.id}`}
                >
                  <Icon className={`h-6 w-6 mx-auto mb-2 ${isSelected ? "text-blue-900" : "text-slate-400"}`} />
                  <p className={`text-xl font-bold ${isSelected ? "text-blue-900" : "text-slate-900"}`}>{pkg.amount}€</p>
                  <p className="text-xs text-slate-500 mt-1">{pkg.label}</p>
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-amber-500 shrink-0" />
                <span className="text-sm text-slate-600 shrink-0">Montant libre :</span>
                <div className="relative flex-1">
                  <Input
                    type="number" min="1" max="1000" step="0.01" placeholder="Montant en €"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelected(null); }}
                    className="pr-8"
                    data-testid="donation-custom-amount"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment method selector */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => setPaymentMethod("stripe")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                paymentMethod === "stripe" ? "border-blue-900 bg-blue-50 text-blue-900" : "border-slate-200 bg-white text-slate-500 hover:border-blue-300"
              }`}
              data-testid="method-stripe"
            >
              <CreditCard className="h-4 w-4" />
              Carte bancaire
            </button>
            <button
              onClick={() => setPaymentMethod("paypal")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                paymentMethod === "paypal" ? "border-[#0070ba] bg-blue-50 text-[#0070ba]" : "border-slate-200 bg-white text-slate-500 hover:border-blue-300"
              }`}
              data-testid="method-paypal"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
              PayPal
            </button>
          </div>

          {/* Payment buttons */}
          {paymentMethod === "stripe" && (
            <Button
              onClick={handleStripe}
              disabled={loading || !amountReady}
              className="w-full bg-blue-900 hover:bg-blue-800 h-12 text-base"
              data-testid="donate-btn"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Redirection vers Stripe...</>
              ) : (
                <><CreditCard className="mr-2 h-5 w-5" />Payer par carte{getAmount() ? ` — ${getAmount()}€` : ""}</>
              )}
            </Button>
          )}

          {paymentMethod === "paypal" && amountReady && (
            <div data-testid="paypal-buttons-container">
              <PayPalButtons
                style={{ layout: "horizontal", color: "blue", shape: "rect", label: "donate", height: 48 }}
                createOrder={createPayPalOrder}
                onApprove={onPayPalApprove}
                onError={() => toast.error("Erreur PayPal")}
                onCancel={() => toast.info("Paiement annulé")}
              />
            </div>
          )}

          {paymentMethod === "paypal" && !amountReady && (
            <div className="text-center py-4 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl" data-testid="paypal-select-amount">
              Choisissez un montant pour voir le bouton PayPal
            </div>
          )}

          <p className="text-xs text-slate-400 text-center mt-4">
            Paiement sécurisé par {paymentMethod === "stripe" ? "Stripe" : "PayPal"}. Aucune donnée bancaire n'est stockée sur nos serveurs.
          </p>
        </main>
      </div>
    </PayPalScriptProvider>
  );
};

export default Donation;
